/**
 * Authentication Validation Schemas
 *
 * Zod schemas for validating authentication-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Login form validation
 * - Registration form validation
 * - Password reset validation
 * - Profile update validation
 * - Password strength validation
 * - Email format validation
 * - Terms acceptance validation
 */

import { z } from './zod';
import { dayjs } from '../utils/date';

import type { TwoFactorSignInMethod } from '../config/two-factor.config';
import type { TwoFactorProof } from '../types/auth';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * Email validation with proper format checking
 */
const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(255, 'Email must be less than 255 characters')
  .toLowerCase()
  .trim();

/**
 * The password rules the client can check. They mirror the backend's
 * `PasswordValidationRules` (`Password::min(12)->letters()->uncompromised()`).
 * `uncompromised` is a breach lookup only the server can run, so it is not
 * here: UIs should present it as "checked when you save", never as met.
 */
export const PASSWORD_MIN_LENGTH = 12;

/** Laravel's `letters()` rule: any Unicode letter (`\pL`). */
const LETTER_PATTERN = /\p{L}/u;

export type PasswordRuleId = 'length' | 'letters';

export function checkPasswordRules(
  password: string
): Record<PasswordRuleId, boolean> {
  return {
    length: password.length >= PASSWORD_MIN_LENGTH,
    letters: LETTER_PATTERN.test(password),
  };
}

/**
 * Password validation — the client-checkable half of the backend rule
 */
const passwordSchema = z
  .string()
  .min(
    PASSWORD_MIN_LENGTH,
    `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
  )
  .regex(LETTER_PATTERN, 'Password must contain at least one letter');

/**
 * Full name validation (for profile updates)
 */
const fullNameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters')
  .regex(
    /^[a-zA-Z\s'-]+$/,
    'Name can only contain letters, spaces, hyphens, and apostrophes'
  )
  .trim();

/**
 * Username validation (alpha_dash:ascii, min:6, max:255, unique)
 */
const usernameSchema = z
  .string()
  .min(6, 'Username must be at least 6 characters long')
  .max(255, 'Username must be less than 255 characters')
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    'Username can only contain letters, numbers, hyphens, and underscores'
  )
  .trim();

/**
 * Age validation (13-120 years)
 */
const ageSchema = z
  .number()
  .int('Age must be a whole number')
  .min(13, 'You must be at least 13 years old')
  .max(120, 'Age must be less than 120 years');

/**
 * Phone number validation (optional)
 */
const phoneSchema = z
  .string()
  .optional()
  .refine(
    (phone) =>
      !phone || /^[+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s\-()]/g, '')),
    'Please enter a valid phone number'
  );

/**
 * Date of birth validation
 */
const dateOfBirthSchema = z
  .string()
  .optional()
  .refine((date) => {
    if (!date) return true;
    const birthDate = dayjs(date, 'YYYY-MM-DD');
    if (!birthDate.isValid()) return true;
    const age = dayjs().diff(birthDate, 'year');
    return age >= 13 && age <= 120;
  }, 'You must be between 13 and 120 years old');

/**
 * Role validation
 */
const roleSchema = z
  .enum(['client', 'trainer'], {
    error: 'Please select a valid role',
  })
  .optional();

/**
 * Gender validation (excluding PREFER_NOT_TO_SAY for registration)
 */
const genderSchema = z
  .enum(
    [
      'female',
      'male',
      'non_binary',
      'transgender',
      'genderqueer',
      'agender',
      'bigender',
      'gender_fluid',
    ],
    {
      error: 'Please select a valid gender option',
    }
  )
  .optional();

/**
 * Gender validation including PREFER_NOT_TO_SAY (for profile updates)
 */
const fullGenderSchema = z
  .enum(
    [
      'female',
      'male',
      'non_binary',
      'transgender',
      'genderqueer',
      'agender',
      'bigender',
      'gender_fluid',
      'prefer_not_to_say',
    ],
    {
      error: 'Please select a valid gender option',
    }
  )
  .optional();

/**
 * Invitation token validation
 */
const invitationTokenSchema = z
  .string()
  .min(1, 'Invitation token is required')
  .max(255, 'Invalid invitation token format');

// =============================================================================
// LOGIN VALIDATION SCHEMAS
// =============================================================================

/**
 * Login form validation schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z
    .string()
    .min(1, 'Password is required')
    .max(128, 'Password must be less than 128 characters'),
  remember_me: z.boolean().optional().default(false),
  device_name: z
    .string()
    .max(100, 'Device name must be less than 100 characters')
    .optional(),
});

// =============================================================================
// REGISTRATION VALIDATION SCHEMAS
// =============================================================================

/**
 * Registration form validation schema (matches Laravel API)
 */
export const registerSchema = z
  .object({
    name: z
      .string()
      .max(255, 'Name must be less than 255 characters')
      .optional(),
    email: emailSchema,
    role: roleSchema,
    gender: fullGenderSchema,
    token: invitationTokenSchema,
    password: passwordSchema,
    password_confirmation: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

/**
 * An emailed verification link's four parts, as read from the website's
 * `/verify-email` query. Each only has to be there: judging the signature is
 * the API's job, and the parts go back exactly as they came.
 */
export const emailVerificationLinkSchema = z.object({
  id: z.string().min(1, 'The link has no account id'),
  hash: z.string().min(1, 'The link has no hash'),
  expires: z.string().min(1, 'The link has no expiry'),
  signature: z.string().min(1, 'The link has no signature'),
});

// =============================================================================
// PASSWORD RESET VALIDATION SCHEMAS
// =============================================================================

/**
 * Password reset request schema
 */
export const passwordResetRequestSchema = z.object({
  email: emailSchema,
});

/**
 * Password reset confirmation schema
 */
export const passwordResetConfirmSchema = z
  .object({
    email: emailSchema,
    token: z.string().min(1, 'Reset token is required'),
    password: passwordSchema,
    password_confirmation: z
      .string()
      .min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

/**
 * Change password schema (for authenticated users)
 */
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: passwordSchema,
    new_password_confirmation: z
      .string()
      .min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.new_password === data.new_password_confirmation, {
    message: 'New passwords do not match',
    path: ['new_password_confirmation'],
  })
  .refine((data) => data.current_password !== data.new_password, {
    message: 'New password must be different from current password',
    path: ['new_password'],
  });

/**
 * Delete account schema — the proof of intent before a permanent, immediate
 * deletion: the current password, or for an account with none, the word
 * DELETE. The server is the source of truth for correctness; this only guards
 * against an empty password or a mistyped word.
 */
export const deleteAccountSchema = z.union([
  z.object({
    password: z.string().min(1, 'Password is required'),
  }),
  z.object({
    confirmation: z.literal('DELETE', {
      error: 'Type DELETE to confirm deleting your account.',
    }),
  }),
]);

// =============================================================================
// PROFILE UPDATE VALIDATION SCHEMAS
// =============================================================================

/**
 * Basic profile update schema
 */
export const updateProfileSchema = z.object({
  name: fullNameSchema.optional(),
  age: ageSchema.optional(),
  gender: fullGenderSchema,
  role: z.enum(['client', 'trainer']).optional(),
});

/**
 * Profile validation for onboarding/first-time setup
 */
export const onboardingProfileSchema = z.object({
  name: fullNameSchema,
  age: ageSchema,
  gender: fullGenderSchema,
  primary_goal: z.enum([
    'lose_weight',
    'gain_weight',
    'build_muscle',
    'improve_endurance',
    'maintain_fitness',
  ]),
  activity_level: z.enum([
    'sedentary',
    'lightly_active',
    'moderately_active',
    'very_active',
    'extremely_active',
  ]),
});

// =============================================================================
// TWO-FACTOR AUTHENTICATION SCHEMAS
// =============================================================================

/**
 * A code from an authenticator app: six digits. Apps show it as "123 456", so
 * spaces are dropped before it's checked, and what's sent is the digits.
 */
export const twoFactorCodeSchema = z.object({
  code: z
    .string()
    .transform((value) => value.replace(/\s/g, ''))
    .pipe(
      z
        .string()
        .regex(
          /^\d{6}$/,
          'Enter the six-digit code from your authenticator app'
        )
    ),
});

/**
 * One of the account's recovery codes, as typed. Whether it's one of theirs is
 * the API's call, so it only has to be there.
 */
export const recoveryCodeSchema = z.object({
  recovery_code: z.string().trim().min(1, 'Enter a recovery code'),
});

// =============================================================================
// DEVICE MANAGEMENT SCHEMAS
// =============================================================================

/**
 * Device name update schema
 */
export const updateDeviceNameSchema = z.object({
  device_id: z.string().min(1, 'Device ID is required'),
  name: z
    .string()
    .min(1, 'Device name is required')
    .max(100, 'Device name must be less than 100 characters')
    .trim(),
});

/**
 * Revoke device schema
 */
export const revokeDeviceSchema = z.object({
  device_id: z.string().min(1, 'Device ID is required'),
});

// =============================================================================
// FORM FIELD VALIDATION SCHEMAS
// =============================================================================

/**
 * Individual field validation schemas for real-time validation
 */
export const fieldSchemas = {
  email: emailSchema,
  password: passwordSchema,
  name: z.string().max(255, 'Name must be less than 255 characters').optional(),
  username: usernameSchema,
  phone: phoneSchema,
  dateOfBirth: dateOfBirthSchema,
  gender: genderSchema,
  fullGender: fullGenderSchema,
  role: roleSchema,
  token: invitationTokenSchema,
} as const;

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Get validation error message for a specific field
 */
export function getFieldError(
  errors: z.ZodError,
  fieldName: string
): string | undefined {
  const fieldError = errors.issues.find((error) => error.path[0] === fieldName);
  return fieldError?.message;
}

/**
 * Check if a field has validation errors
 */
export function hasFieldError(errors: z.ZodError, fieldName: string): boolean {
  return errors.issues.some((error) => error.path[0] === fieldName);
}

/**
 * Get all validation errors as a flat array
 */
export function getAllValidationErrors(errors: z.ZodError): Array<{
  field: string;
  message: string;
}> {
  return errors.issues.map((error) => ({
    field: error.path.join('.'),
    message: error.message,
  }));
}

/**
 * Check a password against the client-checkable backend rules.
 * `score` is the number of rules met; `feedback` lists the unmet ones.
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} {
  const rules = checkPasswordRules(password);
  const feedback: string[] = [];

  if (!rules.length)
    feedback.push(`At least ${PASSWORD_MIN_LENGTH} characters`);
  if (!rules.letters) feedback.push('Includes letters');

  const score = Object.values(rules).filter(Boolean).length;

  return {
    isValid: feedback.length === 0,
    score,
    feedback,
  };
}

/**
 * Validate email format and return detailed feedback
 */
export function validateEmailFormat(email: string): {
  isValid: boolean;
  feedback: string;
} {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = emailRegex.test(email);

  if (isValid) {
    return { isValid: true, feedback: 'Valid email format' };
  }

  if (!email.includes('@')) {
    return { isValid: false, feedback: 'Email must contain @ symbol' };
  }

  if (email.split('@').length !== 2) {
    return {
      isValid: false,
      feedback: 'Email must contain exactly one @ symbol',
    };
  }

  const [local, domain] = email.split('@');
  if (!local || !domain) {
    return {
      isValid: false,
      feedback: 'Email must have local and domain parts',
    };
  }

  if (!domain.includes('.')) {
    return { isValid: false, feedback: 'Domain must contain a dot' };
  }

  return { isValid: false, feedback: 'Invalid email format' };
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type LoginInput = z.input<typeof loginSchema>;
export type LoginOutput = z.output<typeof loginSchema>;
export type RegisterInput = z.input<typeof registerSchema>;
export type RegisterOutput = z.output<typeof registerSchema>;
export type EmailVerificationLinkInput = z.input<
  typeof emailVerificationLinkSchema
>;
export type EmailVerificationLinkOutput = z.output<
  typeof emailVerificationLinkSchema
>;
export type PasswordResetRequestInput = z.input<
  typeof passwordResetRequestSchema
>;
export type PasswordResetRequestOutput = z.output<
  typeof passwordResetRequestSchema
>;
export type PasswordResetConfirmInput = z.input<
  typeof passwordResetConfirmSchema
>;
export type PasswordResetConfirmOutput = z.output<
  typeof passwordResetConfirmSchema
>;
export type ChangePasswordInput = z.input<typeof changePasswordSchema>;
export type ChangePasswordOutput = z.output<typeof changePasswordSchema>;
export type DeleteAccountOutput = z.output<typeof deleteAccountSchema>;
export type UpdateProfileInput = z.input<typeof updateProfileSchema>;
export type UpdateProfileOutput = z.output<typeof updateProfileSchema>;
export type OnboardingProfileInput = z.input<typeof onboardingProfileSchema>;
export type OnboardingProfileOutput = z.output<typeof onboardingProfileSchema>;
export type TwoFactorCodeInput = z.input<typeof twoFactorCodeSchema>;
export type TwoFactorCodeOutput = z.output<typeof twoFactorCodeSchema>;
export type RecoveryCodeInput = z.input<typeof recoveryCodeSchema>;
export type RecoveryCodeOutput = z.output<typeof recoveryCodeSchema>;
export type UpdateDeviceNameInput = z.input<typeof updateDeviceNameSchema>;
export type UpdateDeviceNameOutput = z.output<typeof updateDeviceNameSchema>;
export type RevokeDeviceInput = z.input<typeof revokeDeviceSchema>;
export type RevokeDeviceOutput = z.output<typeof revokeDeviceSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate login data
 * @param data - The data to validate
 * @returns The validated and typed login data
 * @throws ZodError if validation fails
 */
export function validateLogin(data: unknown): LoginOutput {
  return loginSchema.parse(data);
}

/**
 * Safely validate login data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateLogin(data: unknown) {
  return loginSchema.safeParse(data);
}

/**
 * Check if data is valid login data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidLogin(data: unknown): boolean {
  return loginSchema.safeParse(data).success;
}

/**
 * Validate registration data
 * @param data - The data to validate
 * @returns The validated and typed registration data
 * @throws ZodError if validation fails
 */
export function validateRegister(data: unknown): RegisterOutput {
  return registerSchema.parse(data);
}

/**
 * Safely validate registration data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateRegister(data: unknown) {
  return registerSchema.safeParse(data);
}

/**
 * Check if data is valid registration data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidRegister(data: unknown): boolean {
  return registerSchema.safeParse(data).success;
}

/**
 * Validate an emailed verification link's parts
 * @param data - The data to validate
 * @returns The validated and typed link
 * @throws ZodError if validation fails
 */
export function validateEmailVerificationLink(
  data: unknown
): EmailVerificationLinkOutput {
  return emailVerificationLinkSchema.parse(data);
}

/**
 * Safely validate an emailed verification link's parts
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateEmailVerificationLink(data: unknown) {
  return emailVerificationLinkSchema.safeParse(data);
}

/**
 * Check if data is a whole emailed verification link
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidEmailVerificationLink(data: unknown): boolean {
  return emailVerificationLinkSchema.safeParse(data).success;
}

/**
 * Validate password reset request data
 * @param data - The data to validate
 * @returns The validated and typed password reset request data
 * @throws ZodError if validation fails
 */
export function validatePasswordResetRequest(
  data: unknown
): PasswordResetRequestOutput {
  return passwordResetRequestSchema.parse(data);
}

/**
 * Safely validate password reset request data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidatePasswordResetRequest(data: unknown) {
  return passwordResetRequestSchema.safeParse(data);
}

/**
 * Check if data is valid password reset request data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidPasswordResetRequest(data: unknown): boolean {
  return passwordResetRequestSchema.safeParse(data).success;
}

/**
 * Validate password reset confirmation data
 * @param data - The data to validate
 * @returns The validated and typed password reset confirmation data
 * @throws ZodError if validation fails
 */
export function validatePasswordResetConfirm(
  data: unknown
): PasswordResetConfirmOutput {
  return passwordResetConfirmSchema.parse(data);
}

/**
 * Safely validate password reset confirmation data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidatePasswordResetConfirm(data: unknown) {
  return passwordResetConfirmSchema.safeParse(data);
}

/**
 * Check if data is valid password reset confirmation data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidPasswordResetConfirm(data: unknown): boolean {
  return passwordResetConfirmSchema.safeParse(data).success;
}

/**
 * Validate change password data
 * @param data - The data to validate
 * @returns The validated and typed change password data
 * @throws ZodError if validation fails
 */
export function validateChangePassword(data: unknown): ChangePasswordOutput {
  return changePasswordSchema.parse(data);
}

/**
 * Safely validate change password data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateChangePassword(data: unknown) {
  return changePasswordSchema.safeParse(data);
}

/**
 * Safely validate delete account data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateDeleteAccount(data: unknown) {
  return deleteAccountSchema.safeParse(data);
}

/**
 * Check if data is valid change password data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidChangePassword(data: unknown): boolean {
  return changePasswordSchema.safeParse(data).success;
}

/**
 * Validate profile update data
 * @param data - The data to validate
 * @returns The validated and typed profile update data
 * @throws ZodError if validation fails
 */
export function validateUpdateProfile(data: unknown): UpdateProfileOutput {
  return updateProfileSchema.parse(data);
}

/**
 * Safely validate profile update data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateProfile(data: unknown) {
  return updateProfileSchema.safeParse(data);
}

/**
 * Check if data is valid profile update data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateProfile(data: unknown): boolean {
  return updateProfileSchema.safeParse(data).success;
}

/**
 * Validate onboarding profile data
 * @param data - The data to validate
 * @returns The validated and typed onboarding profile data
 * @throws ZodError if validation fails
 */
export function validateOnboardingProfile(
  data: unknown
): OnboardingProfileOutput {
  return onboardingProfileSchema.parse(data);
}

/**
 * Safely validate onboarding profile data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateOnboardingProfile(data: unknown) {
  return onboardingProfileSchema.safeParse(data);
}

/**
 * Check if data is valid onboarding profile data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidOnboardingProfile(data: unknown): boolean {
  return onboardingProfileSchema.safeParse(data).success;
}

/**
 * Validate an authenticator app's code
 * @param data - The data to validate
 * @returns The validated code, as digits
 * @throws ZodError if validation fails
 */
export function validateTwoFactorCode(data: unknown): TwoFactorCodeOutput {
  return twoFactorCodeSchema.parse(data);
}

/**
 * Safely validate an authenticator app's code
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateTwoFactorCode(data: unknown) {
  return twoFactorCodeSchema.safeParse(data);
}

/**
 * Check if data is an authenticator app's code
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidTwoFactorCode(data: unknown): boolean {
  return twoFactorCodeSchema.safeParse(data).success;
}

/**
 * Validate a recovery code
 * @param data - The data to validate
 * @returns The validated code, trimmed
 * @throws ZodError if validation fails
 */
export function validateRecoveryCode(data: unknown): RecoveryCodeOutput {
  return recoveryCodeSchema.parse(data);
}

/**
 * Safely validate a recovery code
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateRecoveryCode(data: unknown) {
  return recoveryCodeSchema.safeParse(data);
}

/**
 * Check if data is a recovery code
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidRecoveryCode(data: unknown): boolean {
  return recoveryCodeSchema.safeParse(data).success;
}

/**
 * What's typed at a two-factor step, as the proof to send, or why it isn't one
 * yet: a code from the authenticator app (six digits, spaces dropped), or a
 * recovery code (trimmed), as the step asks.
 */
export function readTwoFactorProof(
  method: TwoFactorSignInMethod,
  typed: string
): { proof: TwoFactorProof } | { problem: string } {
  const parsed =
    method === 'code'
      ? twoFactorCodeSchema.safeParse({ code: typed })
      : recoveryCodeSchema.safeParse({ recovery_code: typed });
  return parsed.success
    ? { proof: parsed.data }
    : { problem: parsed.error.issues[0]?.message ?? parsed.error.message };
}

/**
 * Validate device name update data
 * @param data - The data to validate
 * @returns The validated and typed device name update data
 * @throws ZodError if validation fails
 */
export function validateUpdateDeviceName(
  data: unknown
): UpdateDeviceNameOutput {
  return updateDeviceNameSchema.parse(data);
}

/**
 * Safely validate device name update data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateDeviceName(data: unknown) {
  return updateDeviceNameSchema.safeParse(data);
}

/**
 * Check if data is valid device name update data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateDeviceName(data: unknown): boolean {
  return updateDeviceNameSchema.safeParse(data).success;
}

/**
 * Validate device revocation data
 * @param data - The data to validate
 * @returns The validated and typed device revocation data
 * @throws ZodError if validation fails
 */
export function validateRevokeDevice(data: unknown): RevokeDeviceOutput {
  return revokeDeviceSchema.parse(data);
}

/**
 * Safely validate device revocation data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateRevokeDevice(data: unknown) {
  return revokeDeviceSchema.safeParse(data);
}

/**
 * Check if data is valid device revocation data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidRevokeDevice(data: unknown): boolean {
  return revokeDeviceSchema.safeParse(data).success;
}
